import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AlertDialog } from 'common/components/AlertDialog';
import { Button } from 'common/components/Button';
import { TextField } from 'common/forms/Field';
import { CertificateOfferEventSelectField } from 'offer/forms/Field';
import {
    SkillCertificateSelectField,
    SkillSelectField,
} from 'skill/forms/Field';
import { TCertificateOption } from 'certificate/types';
import {
    CreateOfferEventEnrollmentsDocument,
    IBasicOfferEventListItemForCertificateFragment,
    IContentTypeValue,
    IEmailTypeEnum,
    ISkillInputOptionFragment,
    SendEmailDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { getSkillCertificateOfferEventDialogValidator } from 'skill/forms/validators/skillCertificateOfferEventDialog';
import { ESkillCertificateOfferEventDialogMode } from 'skill/types';

interface IProps {
    open: boolean;
    userIds: string[];
    mode?: ESkillCertificateOfferEventDialogMode;
    skill?: ISkillInputOptionFragment | null;
    certificate?: TCertificateOption | null;
    skillIds?: string[];
    onClose: () => void;
    onComplete?: () => void;
}

interface IFormValues {
    skill: ISkillInputOptionFragment | null;
    certificate: TCertificateOption | null;
    offerEvent: IBasicOfferEventListItemForCertificateFragment | null;
    message: string;
}

const EMPTY_VALUES: IFormValues = {
    skill: null,
    certificate: null,
    offerEvent: null,
    message: '',
};

export const SkillCertificateOfferEventDialog = ({
    open,
    userIds,
    mode = ESkillCertificateOfferEventDialogMode.Enrollment,
    skill: skillProp,
    certificate: certificateProp,
    skillIds,
    onClose,
    onComplete,
}: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();

    // Keep the most recent mode while the dialog is open so the UI does not
    // collapse to the default mode during the close transition when the
    // consumer clears its mode state.
    const lastModeRef = useRef<ESkillCertificateOfferEventDialogMode>(mode);
    if (open) lastModeRef.current = mode;
    const effectiveMode = open ? mode : lastModeRef.current;

    const isNotify =
        effectiveMode === ESkillCertificateOfferEventDialogMode.Notify;
    const hasSkillProp = !!skillProp;
    const hasCertificateProp = !!certificateProp;
    // With a locked certificate there is no skill to pick, so the skill field
    // is hidden and skill is not required to submit.
    const requireSkill = !hasCertificateProp;
    const translationPrefix = isNotify
        ? 'skillCertificateOfferEventDialog.notify'
        : 'skillCertificateOfferEventDialog';

    const initialValues: IFormValues = useMemo(
        () => ({
            ...EMPTY_VALUES,
            skill: skillProp ?? null,
            certificate: certificateProp ?? null,
        }),
        [skillProp, certificateProp]
    );

    const resolver = useMemo(
        () =>
            zodResolver(
                getSkillCertificateOfferEventDialogValidator(
                    effectiveMode,
                    requireSkill
                )
            ),
        [effectiveMode, requireSkill]
    );

    const { control, handleSubmit, reset, setValue } = useForm<IFormValues>({
        resolver,
        defaultValues: initialValues,
    });

    const skill = useWatch({ control, name: 'skill' });
    const certificate = useWatch({ control, name: 'certificate' });
    const offerEvent = useWatch({ control, name: 'offerEvent' });
    const message = useWatch({ control, name: 'message' });
    const skillId = skill?.id;
    const certificateId = certificate?.id;

    const [createEnrollments, { loading: enrolling }] = useMutation(
        CreateOfferEventEnrollmentsDocument
    );

    const [sendEmail, { loading: sendingEmail }] =
        useMutation(SendEmailDocument);

    const noResultsLabel = translate(
        'skillCertificateOfferEventDialog.noResultsFound'
    );

    // Skip the dependent-field clear on the first render so it can never wipe
    // pre-populated initial values; only clear when the skill actually changes.
    const isInitialSkillRender = useRef(true);

    useEffect(() => {
        if (isInitialSkillRender.current) {
            isInitialSkillRender.current = false;

            return;
        }

        // Skip when the certificate is locked — it must not be cleared.
        if (hasCertificateProp) return;

        setValue('certificate', null);
        setValue('offerEvent', null);
    }, [skillId, setValue, hasCertificateProp]);

    useEffect(() => {
        setValue('offerEvent', null);
    }, [certificateId, setValue]);

    const wasOpen = useRef(false);

    useEffect(() => {
        if (open && !wasOpen.current) reset(initialValues);
        wasOpen.current = open;
    }, [open, reset, initialValues]);

    const handleSubmitEnrollment = async (values: IFormValues) => {
        if (!userIds.length || !values.offerEvent) return;

        try {
            await createEnrollments({
                variables: {
                    offerEventId: values.offerEvent.id,
                    users: userIds,
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate('skillCertificateOfferEventDialog.successMessage', {
                count: userIds.length,
            }),
            { variant: 'success' }
        );

        onComplete?.();
        onClose();
    };

    const handleSubmitNotify = async (values: IFormValues) => {
        if (!userIds.length || !values.certificate) return;

        const relatedObjects = [
            ...(values.skill
                ? [{ id: values.skill.id, type: IContentTypeValue.Skill }]
                : []),
            {
                id: values.certificate.id,
                type: IContentTypeValue.Certificate,
            },
        ];

        let result;

        try {
            result = await sendEmail({
                variables: {
                    emailType: IEmailTypeEnum.OfferEventInvite,
                    message: values.message,
                    userIds,
                    metadata: { relatedObjects },
                    ...(values.offerEvent
                        ? {
                              relatedType: IContentTypeValue.OfferEvent,
                              relatedId: values.offerEvent.id,
                          }
                        : {}),
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        const { succeededIds = [], failedIds = [] } =
            result.data?.sendEmail?.response || {};

        if (succeededIds.length) {
            displaySnackbar(
                translate(
                    'skillCertificateOfferEventDialog.notify.successMessage',
                    { count: succeededIds.length }
                ),
                { variant: 'success' }
            );
        }

        if (failedIds.length) {
            displaySnackbar(
                translate(
                    'skillCertificateOfferEventDialog.notify.failedMessage',
                    {
                        count: failedIds.length,
                    }
                ),
                { variant: 'error' }
            );
        }

        onComplete?.();
        onClose();
    };

    const onSubmit = isNotify ? handleSubmitNotify : handleSubmitEnrollment;
    const submitting = isNotify ? sendingEmail : enrolling;
    const skillSatisfied = !requireSkill || !!skill;
    const missingSkillOrCertificate = !skillSatisfied || !certificate;
    const submitDisabled = isNotify
        ? missingSkillOrCertificate || !message?.trim()
        : missingSkillOrCertificate || !offerEvent;

    return (
        <AlertDialog
            fullWidth
            actions={
                <Stack direction="row" spacing={2}>
                    <Button
                        color="primary"
                        disabled={submitDisabled}
                        loading={submitting}
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                    >
                        {translate(`${translationPrefix}.confirmButton`)}
                    </Button>

                    <Button
                        disabled={submitting}
                        variant="contained"
                        onClick={onClose}
                    >
                        {translate(
                            'skillCertificateOfferEventDialog.cancelButton'
                        )}
                    </Button>
                </Stack>
            }
            maxWidth="sm"
            open={open}
            title={translate(`${translationPrefix}.title`)}
            onClose={onClose}
        >
            <Typography>{translate(`${translationPrefix}.text`)}</Typography>

            <Box component="form" sx={{ mt: 1 }}>
                {!hasSkillProp && !hasCertificateProp && (
                    <SkillSelectField
                        control={control}
                        ids={skillIds}
                        label={translate(
                            'skillCertificateOfferEventDialog.skillField'
                        )}
                        name="skill"
                        noOptionsText={noResultsLabel}
                    />
                )}

                {!hasCertificateProp && (
                    <SkillCertificateSelectField
                        control={control}
                        label={translate(
                            'skillCertificateOfferEventDialog.certificateField'
                        )}
                        name="certificate"
                        noOptionsText={noResultsLabel}
                        skillId={skillId}
                    />
                )}

                <CertificateOfferEventSelectField
                    canEnrollParticipants={isNotify ? undefined : true}
                    certificateId={certificateId}
                    control={control}
                    enrollableForUser={
                        isNotify && userIds.length === 1
                            ? userIds[0]
                            : undefined
                    }
                    label={translate(
                        'skillCertificateOfferEventDialog.offerField'
                    )}
                    name="offerEvent"
                    noOptionsText={noResultsLabel}
                />

                {isNotify && (
                    <TextField
                        multiline
                        control={control}
                        label={translate('emailMessage')}
                        name="message"
                        rows={4}
                        sx={{
                            '.MuiOutlinedInput-input': {
                                minHeight: 0,
                                resize: 'vertical',
                            },
                        }}
                    />
                )}
            </Box>
        </AlertDialog>
    );
};
